import { useEffect, useState } from "react";
import { toast } from "sonner";
import { partnerApi, bpsToPct, centsToUsd } from "@/lib/partner";
import { workspaceApi } from "@/lib/workspace";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AlertCircle, ArrowRight, Copy, Loader2, RefreshCw } from "lucide-react";
import { PartnerShell } from "@/components/partner/PartnerShell";
import { useLocale } from "@/i18n/useLocale";
import promoEn from "@/data/partner_promo_content.en.json";
import promoAr from "@/data/partner_promo_content.ar.json";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// The persistent top-nav + footer live inside <PartnerShell>. Every state
// (loading, error, pending, suspended, active) renders inside the same shell
// so the user always sees the nav — no dead-end white pages.
const Shell = ({ children, testId = "partner-dashboard-shell" }) => (
  <PartnerShell testId={testId}>{children}</PartnerShell>
);

const Kpi = ({ label, value, sub, testId }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p>
    <p className="mt-1 text-2xl font-light tabular-nums text-white">{value}</p>
    {sub ? <p className="mt-1 text-[11px] text-white/45">{sub}</p> : null}
  </div>
);

const InfoRow = ({ label, value, mono, testId }) => (
  <div className="flex items-start justify-between gap-4 border-t border-white/5 py-2 first:border-t-0 first:pt-0">
    <span className="text-[11px] uppercase tracking-wider text-white/40">{label}</span>
    <span
      data-testid={testId}
      className={`max-w-[65%] truncate text-right text-sm text-white/85 ${mono ? "font-mono" : ""}`}
    >
      {value || <span className="text-white/30">—</span>}
    </span>
  </div>
);

export default function PartnerDashboard() {
  const [data, setData] = useState(null);
  const [marketing, setMarketing] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const { workspace, memberships, refreshSession } = useAuth() || {};
  const { locale } = useLocale();
  const isAr = String(locale || "en").toLowerCase().startsWith("ar");
  const reconsentCopy = (isAr ? promoAr : promoEn).reconsent;
  const [reconsentSaving, setReconsentSaving] = useState(false);
  const nav = useNavigate();
  const hasWorkspace = !!(workspace || (memberships && memberships.length > 0));
  const [ctaDismissed, setCtaDismissed] = useState(() => {
    try { return localStorage.getItem("tp_partner_workspace_cta_dismissed") === "1"; } catch { return false; }
  });
  const [trialStarting, setTrialStarting] = useState(false);
  const dismissCta = () => {
    setCtaDismissed(true);
    try { localStorage.setItem("tp_partner_workspace_cta_dismissed", "1"); } catch { /* ignore */ }
  };
  const startTrial = async () => {
    if (trialStarting) return;
    setTrialStarting(true);
    try {
      await workspaceApi.startTrial({});
      await refreshSession?.();
      toast.success("Your 14-day trial has started. Welcome!");
      nav("/dashboard");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && detail.error === "workspace_exists") {
        toast.message("You already have a workspace.");
        await refreshSession?.();
        nav("/dashboard");
      } else {
        toast.error(typeof detail === "string" ? detail : "We couldn&rsquo;t start your trial. Please try again.");
      }
    } finally { setTrialStarting(false); }
  };

  const load = () => {
    setLoading(true); setErr("");
    partnerApi.dashboard()
      .then((d) => { setData(d); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "load_failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (data?.partner?.status === "active") partnerApi.marketingLink().then(setMarketing).catch(() => {});
  }, [data?.partner?.status]);

  // ------------------------------------------------------------------
  // Loading + terminal error states — always inside Shell so the page is
  // never a blank white viewport.
  // ------------------------------------------------------------------
  if (loading && !data) {
    return (
      <Shell testId="partner-dashboard-loading">
        <div className="flex items-center gap-2 text-white/50">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your partner dashboard…
        </div>
      </Shell>
    );
  }

  if (err === "not_a_partner") {
    return (
      <Shell testId="partner-not-a-partner">
        <div className="mx-auto max-w-xl rounded-2xl border border-white/10 bg-white/[0.02] p-8">
          <h1 className="text-2xl font-light">You haven&rsquo;t applied to the Partner Program yet</h1>
          <p className="mt-3 text-sm leading-relaxed text-white/60">
            Earn commissions by referring TapPresence to your network. Apply in a few minutes —
            approvals are usually same-day.
          </p>
          <Link
            to="/partners/apply"
            data-testid="partner-not-a-partner-apply"
            className="mt-6 inline-flex items-center rounded-full bg-[#D6A653] px-5 py-2.5 text-sm font-medium text-black hover:bg-[#c99a49]"
          >
            Apply now <ArrowRight className="ml-1.5 h-4 w-4" />
          </Link>
        </div>
      </Shell>
    );
  }

  if (err) {
    return (
      <Shell testId="partner-dashboard-error">
        <div className="mx-auto max-w-lg rounded-2xl border border-red-500/30 bg-red-500/8 p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 text-red-300" />
            <div>
              <p className="text-sm font-medium text-red-100">We couldn&rsquo;t load your partner dashboard.</p>
              <p className="mt-1 text-xs text-red-200/70">Error code: <code className="font-mono">{err}</code></p>
              <button
                onClick={load}
                data-testid="partner-dashboard-retry"
                className="mt-4 inline-flex items-center rounded-full border border-red-400/40 bg-red-500/10 px-4 py-2 text-xs text-red-100 hover:bg-red-500/20"
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Retry
              </button>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // data is guaranteed populated below.
  const p = data?.partner || {};
  const status = p.status;

  // ------------------------------------------------------------------
  // Pending / suspended / rejected / revoked branches — the "unlocks after
  // approval" surface. Referral link + payouts are NOT shown here.
  // ------------------------------------------------------------------
  if (status !== "active") {
    return (
      <Shell testId={`partner-status-${status}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h1 className="text-3xl font-light tracking-tight">
              {status === "pending" && "Thanks for applying to the TapPresence Partner Program"}
              {status === "suspended" && "Your partner account is suspended"}
              {status === "rejected" && "Application not approved"}
              {status === "revoked" && "Your partner account has been revoked"}
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/60">
              {status === "pending" && (
                <>Your application is under review &mdash; we usually respond within one business day. We&rsquo;ll email you at{" "}
                  <span className="text-white/80">{p.payout_email || "your registered email"}</span>{" "}
                  as soon as your Partner account is active.</>
              )}
              {status === "suspended" && (p.suspended_reason
                ? <>Reason from our team: <span className="text-white/80">{p.suspended_reason}</span>. Contact support if you believe this is an error.</>
                : <>Contact support if you believe this is an error.</>)}
              {status === "rejected" && (p.rejection_reason
                ? <>Reason from our team: <span className="text-white/80">{p.rejection_reason}</span>. You may reply to our team by email.</>
                : <>Please reach out if you&rsquo;d like to discuss.</>)}
              {status === "revoked" && (p.revoked_reason
                ? <>Reason from our team: <span className="text-white/80">{p.revoked_reason}</span>.</>
                : <>Please reach out if you have questions.</>)}
            </p>
          </div>
          <p className="text-xs text-white/45">
            Code: <span className="font-mono text-[#D6A653]">{p.partner_code || "—"}</span>
          </p>
        </div>

        {/* Submitted application card — echo back what admin will see */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="partner-application-echo">
          <p className="text-[11px] uppercase tracking-wider text-white/40">Your submitted application</p>
          <div className="mt-4 grid gap-6 md:grid-cols-2">
            <div>
              <InfoRow label="Partner type" value={p.partner_type} testId="echo-type" />
              <InfoRow label="Full name" value={p.full_name} testId="echo-name" />
              <InfoRow label="Country" value={p.country} testId="echo-country" />
              <InfoRow label="Payout email" value={p.payout_email} testId="echo-payout-email" />
              <InfoRow label="Applied at" value={p.applied_at?.slice(0, 10)} testId="echo-applied-at" />
            </div>
            <div>
              {p.partner_type === "individual" && (
                <InfoRow label="Promo channels" value={(p.promo_channels || []).join(", ")} testId="echo-promo-channels" />
              )}
              {p.partner_type === "creator" && (
                <>
                  <InfoRow label="Audience size" value={p.audience_size} testId="echo-audience-size" />
                  <InfoRow label="Primary platforms" value={(p.primary_platforms || []).join(", ") || p.primary_platform} testId="echo-primary-platforms" />
                  <InfoRow label="Content niche" value={p.content_niche} testId="echo-content-niche" />
                  <InfoRow label="Website / handle" value={p.website_or_handle} testId="echo-website" />
                </>
              )}
              {p.partner_type === "agency" && (
                <>
                  <InfoRow label="Company name" value={p.company_name} testId="echo-company-name" />
                  <InfoRow label="Company website" value={p.company_website} testId="echo-company-website" />
                  <InfoRow label="Team size" value={p.team_size} testId="echo-team-size" />
                  <InfoRow label="Client count" value={p.client_count_estimate} testId="echo-client-count" />
                  <InfoRow label="Services offered" value={p.services_offered} testId="echo-services" />
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          {status === "pending" && (
            <Link
              to="/partners/apply"
              data-testid="partner-edit-application"
              className="rounded-full border border-white/15 px-4 py-2 text-sm text-white/80 hover:bg-white/5"
            >
              Edit application
            </Link>
          )}
          <a
            href="mailto:partners@tappresence.com"
            data-testid="partner-contact-support"
            className="rounded-full bg-[#D6A653] px-5 py-2 text-sm font-medium text-black hover:bg-[#c99a49]"
          >
            Contact support
          </a>
        </div>

        {/* Locked / unlocks-after-approval hint tiles */}
        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {["Referral link", "Commissions", "Payouts", "Marketing kit"].map((label) => (
            <div key={label} className="rounded-xl border border-white/8 bg-white/[0.02] p-4 opacity-50" data-testid={`locked-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p>
              <p className="mt-1 text-xs text-white/45">Unlocks after approval</p>
            </div>
          ))}
        </div>
      </Shell>
    );
  }

  // ------------------------------------------------------------------
  // Active partner — the real dashboard.
  // ------------------------------------------------------------------
  const earnings = data?.earnings || {};
  const tier = data?.tier || {};

  const copyLink = () => {
    if (!marketing?.link) return;
    navigator.clipboard.writeText(marketing.link);
    toast.success("Referral link copied");
  };

  // Re-consent gating: when we bump PARTNER_TOS_VERSION, active partners see a
  // blocking dialog until they re-accept. tos_version_current comes from the
  // dashboard payload; the partner's currently-accepted version is on p.
  const tosCurrent = data?.tos_version_current;
  const tosAccepted = p.tos_version;
  const needsReconsent = Boolean(tosCurrent && tosAccepted && tosCurrent !== tosAccepted);

  const submitReconsent = async () => {
    if (reconsentSaving || !tosCurrent) return;
    setReconsentSaving(true);
    try {
      await partnerApi.acceptTos(tosCurrent);
      toast.success(reconsentCopy.agreeCta);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || reconsentCopy.errorGeneric);
    } finally {
      setReconsentSaving(false);
    }
  };

  return (
    <Shell testId="partner-dashboard-page">
      {needsReconsent ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="partner-reconsent-title"
          data-testid="partner-reconsent-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          dir={isAr ? "rtl" : "ltr"}
        >
          <div className="w-full max-w-md rounded-2xl border border-[#D6A653]/30 bg-[#0B0D12] p-6 shadow-2xl">
            <h2 id="partner-reconsent-title" className="text-lg font-medium text-white" data-testid="partner-reconsent-title">
              {reconsentCopy.title}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/70" data-testid="partner-reconsent-body">
              {reconsentCopy.body}
            </p>
            <p className="mt-2 text-xs text-white/50">
              <Link
                to="/partners/terms"
                target="_blank"
                rel="noreferrer"
                data-testid="partner-reconsent-read-terms"
                className="text-[#D6A653] underline underline-offset-4"
              >
                {reconsentCopy.readTerms}
              </Link>
              <span className="mx-2">·</span>
              <span className="font-mono text-white/40">v{tosCurrent}</span>
            </p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={submitReconsent}
                disabled={reconsentSaving}
                data-testid="partner-reconsent-agree"
                className="inline-flex items-center gap-2 rounded-full bg-[#D6A653] px-5 py-2 text-sm font-medium text-black hover:bg-[#c99a49] disabled:opacity-60"
              >
                {reconsentSaving ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {reconsentCopy.agreeing}
                  </>
                ) : reconsentCopy.agreeCta}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-3xl font-light tracking-tight">Partner Dashboard</h1>
        <p className="text-xs text-white/45">Code: <span className="font-mono text-[#D6A653]">{p.partner_code}</span></p>
      </div>

      {!p.payout_ready ? (
        <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="partner-payout-not-ready">
          Complete your payout details before your first payout. <Link to="/partner/payouts" className="underline">Update payout info</Link>
        </div>
      ) : null}

      {!hasWorkspace && !ctaDismissed ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#D6A653]/25 bg-[#D6A653]/6 p-4 text-sm" data-testid="partner-workspace-cta">
          <div className="min-w-0">
            <p className="font-medium text-white">Want to use TapPresence for your own business?</p>
            <p className="mt-0.5 text-xs text-white/60">
              Your Partner account is free forever. If you also want to publish your own card and capture leads,
              start a 14-day trial — separate from your Partner earnings.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button" onClick={startTrial} disabled={trialStarting}
              data-testid="partner-workspace-cta-start"
              className="rounded-full bg-[#D6A653] px-4 py-2 text-xs font-medium text-black hover:bg-[#c99a49] disabled:opacity-60"
            >{trialStarting ? "Starting…" : "Start a 14-day trial (uses your current account)"}</button>
            <button onClick={dismissCta} data-testid="partner-workspace-cta-dismiss"
              className="rounded-full border border-white/15 px-3 py-2 text-xs text-white/55 hover:text-white">Not now</button>
          </div>
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi label="Pending" value={centsToUsd(earnings.pending || 0)} testId="kpi-pending" />
        <Kpi label="Approved" value={centsToUsd(earnings.approved || 0)} testId="kpi-approved" />
        <Kpi label="Payable" value={centsToUsd(earnings.payable || 0)} testId="kpi-payable" />
        <Kpi label="Paid" value={centsToUsd(earnings.paid || 0)} testId="kpi-paid" />
        <Kpi label="Reversed" value={centsToUsd(earnings.reversed || 0)} testId="kpi-reversed" />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-5" data-testid="tier-panel">
          <p className="text-xs uppercase tracking-wider text-white/45">Current tier</p>
          <p className="mt-2 text-4xl font-light text-[#D6A653]">{bpsToPct(tier.rate_bps || 0)}</p>
          <p className="mt-1 text-sm text-white/60">
            {data?.active_seats_count ?? 0} active {data?.active_seats_count === 1 ? "seat" : "seats"} across{" "}
            {data?.active_workspaces_count ?? 0} workspace{data?.active_workspaces_count === 1 ? "" : "s"}
          </p>
          {data?.next_tier_at ? (
            <div className="mt-4">
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/[0.05]">
                <div className="h-full bg-[#D6A653]" style={{ width: `${data.progress_pct ?? 0}%` }} />
              </div>
              <p className="mt-2 text-[11px] text-white/45">
                {data.next_tier_at - (data.active_seats_count || 0)} more seat
                {data.next_tier_at - (data.active_seats_count || 0) === 1 ? "" : "s"} to reach{" "}
                {bpsToPct(data.next_tier_rate_bps || 0)}
              </p>
            </div>
          ) : (
            <p className="mt-4 text-[11px] text-white/40">You&rsquo;re at the top tier — {bpsToPct(tier.rate_bps || 0)}.</p>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-5" data-testid="marketing-panel">
          <p className="text-xs uppercase tracking-wider text-white/45">Your referral link</p>
          {marketing?.link ? (
            <>
              <code className="mt-2 block truncate rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-xs text-white/70" data-testid="marketing-link">{marketing.link}</code>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button onClick={copyLink} data-testid="copy-link"
                  className="inline-flex items-center rounded-lg border border-[#D6A653]/50 bg-[#D6A653]/10 px-3 py-1.5 text-xs text-[#D6A653]">
                  <Copy className="mr-1.5 h-3 w-3" /> Copy link
                </button>
                <a href={`${REACT_APP_BACKEND_URL}${marketing.qr_url}`} target="_blank" rel="noreferrer" className="text-xs text-white/50 underline">Download QR</a>
              </div>
              <img src={`${REACT_APP_BACKEND_URL}${marketing.qr_url}`} alt="Referral QR"
                data-testid="marketing-qr" className="mt-4 h-32 w-32 rounded-lg border border-white/10 bg-white/[0.02]" />
            </>
          ) : (<p className="mt-2 text-sm text-white/50">Loading link…</p>)}
        </div>
      </div>
    </Shell>
  );
}
